import React, { useEffect, useState } from "react";
import SettingsLayout from "@/components/SettingsLayout";
import api, { formatApiError } from "@/lib/api";
import { Palette } from "lucide-react";

// SettingsLayout used to be inlined here; it's now the canonical
// component at /components/SettingsLayout. Re-exported for legacy
// imports (4 sibling pages reference `@/pages/SettingsBranding`).
export { default as SettingsLayout } from "@/components/SettingsLayout";

export default function SettingsBranding() {
  const [org, setOrg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get("/orgs/me");
      setOrg(data);
    } catch (e) { setErr(formatApiError(e)); }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    setErr("");
    try {
      await api.patch("/orgs/me/branding", {
        primary_color: org.branding?.primary_color,
        accent_color: org.branding?.accent_color,
        welcome_message: org.branding?.welcome_message,
      });
      await load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setSaving(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/orgs/me/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      await load();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setSaving(false); }
  };

  if (!org) return <SettingsLayout><div className="text-navy-500">Loading…</div></SettingsLayout>;

  const b = org.branding || {};

  return (
    <SettingsLayout>
      <div className="grid lg:grid-cols-2 gap-6" data-testid="settings-branding-page">
        <div className="eb-card p-6 space-y-4">
          <h2 className="font-semibold">Logo & background</h2>
          <div>
            <label className="eb-label">Logo (PNG/JPG/SVG, ≤2 MB)</label>
            <input type="file" accept="image/png,image/jpeg,image/svg+xml" onChange={(e) => upload("logo", e.target.files?.[0])} data-testid="branding-logo-input" />
            {b.logo_url && <img src={b.logo_url} alt="logo" className="mt-3 h-12 max-w-[160px] object-contain border border-navy-100 rounded-md p-2" />}
          </div>
          <div>
            <label className="eb-label">Background image (JPG/PNG, ≤4 MB)</label>
            <input type="file" accept="image/png,image/jpeg" onChange={(e) => upload("background", e.target.files?.[0])} data-testid="branding-background-input" />
            {b.background_url && <img src={b.background_url} alt="bg" className="mt-3 h-24 w-full object-cover rounded-md" />}
          </div>
        </div>

        <div className="eb-card p-6 space-y-4">
          <h2 className="font-semibold">Colours & message</h2>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="eb-label">Primary</label>
              <div className="flex items-center gap-2">
                <input type="color" value={b.primary_color || "#1B2A4A"} onChange={(e) => setOrg({ ...org, branding: { ...b, primary_color: e.target.value } })} className="h-10 w-14 rounded-md border border-navy-200" data-testid="branding-primary-color" />
                <input type="text" value={b.primary_color || "#1B2A4A"} onChange={(e) => setOrg({ ...org, branding: { ...b, primary_color: e.target.value } })} className="eb-input flex-1 font-mono text-sm" />
              </div>
            </div>
            <div>
              <label className="eb-label">Accent</label>
              <div className="flex items-center gap-2">
                <input type="color" value={b.accent_color || "#FF6B35"} onChange={(e) => setOrg({ ...org, branding: { ...b, accent_color: e.target.value } })} className="h-10 w-14 rounded-md border border-navy-200" data-testid="branding-accent-color" />
                <input type="text" value={b.accent_color || "#FF6B35"} onChange={(e) => setOrg({ ...org, branding: { ...b, accent_color: e.target.value } })} className="eb-input flex-1 font-mono text-sm" />
              </div>
            </div>
          </div>
          <div>
            <label className="eb-label">Welcome message (max 200 chars)</label>
            <textarea
              maxLength={200}
              value={b.welcome_message || ""}
              onChange={(e) => setOrg({ ...org, branding: { ...b, welcome_message: e.target.value } })}
              className="eb-input min-h-[88px]"
              placeholder="Welcome to {site_name}"
              data-testid="branding-welcome-input"
            />
          </div>
          {err && <div className="text-sm text-danger">{err}</div>}
          <button onClick={save} disabled={saving} className="btn-accent" data-testid="branding-save-btn">{saving ? "Saving…" : "Save"}</button>
        </div>

        <div className="eb-card p-6 lg:col-span-2">
          <h2 className="font-semibold mb-4">Live preview (kiosk landing)</h2>
          <div className="rounded-2xl overflow-hidden text-white p-10" style={{ background: `linear-gradient(135deg, ${b.primary_color || "#1B2A4A"} 0%, #0F1A2E 100%)` }} data-testid="branding-preview">
            <div className="text-sm opacity-70 mb-1">{org.name}</div>
            <div className="text-4xl font-bold">{b.welcome_message || `Welcome to ${org.name}`}</div>
            <button className="mt-6 inline-flex items-center gap-2 rounded-lg px-5 py-3 font-semibold text-white" style={{ background: b.accent_color || "#FF6B35" }}>
              Sign in
            </button>
          </div>
        </div>
      </div>
    </SettingsLayout>
  );
}
